import { useState, useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, DollarSign, Package, Layers, ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useModalDrag } from '../../hooks/useModalDrag';
import { useResourceCanvasOverview, type ResourceUsageRow, type FieldColumn } from '../hooks/useResourceCanvasOverview';
import { formatAggregatedValue, computeAggregate } from '../../lib/formatFieldValue';

export interface ResourceCanvasOverviewPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type SortDirection = 'asc' | 'desc';
interface SortState {
  field: string; // 'name' | 'category' | 'entityCount' | fieldId
  direction: SortDirection;
}

export const ResourceCanvasOverviewPanel = memo(function ResourceCanvasOverviewPanel({ isOpen, onClose }: ResourceCanvasOverviewPanelProps) {
  const { t } = useTranslation();
  const { modalRef, position, isDragging, handleMouseDown, handleDoubleClick } = useModalDrag();
  const overview = useResourceCanvasOverview();

  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [sort, setSort] = useState<SortState>({ field: 'name', direction: 'asc' });
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleSort = useCallback((field: string) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const toggleExpand = useCallback((resourceId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  }, []);

  // Filter rows
  const filteredRows = useMemo(() => {
    let rows = overview.rows;

    if (searchText) {
      const lower = searchText.toLowerCase();
      rows = rows.filter(r =>
        r.resource.name.toLowerCase().includes(lower) ||
        r.resource.category.toLowerCase().includes(lower)
      );
    }

    if (categoryFilter) {
      rows = rows.filter(r => r.resource.category === categoryFilter);
    }

    if (typeFilter) {
      rows = rows.filter(r =>
        r.resource.fields.some(f => f.type === typeFilter && f.type !== 'text' && typeof f.value === 'number')
      );
    }

    return rows;
  }, [overview.rows, searchText, categoryFilter, typeFilter]);

  // Filter field columns by type filter
  const visibleColumns = useMemo(() => {
    if (!typeFilter) return overview.fieldColumns;
    return overview.fieldColumns.filter(c => c.type === typeFilter);
  }, [overview.fieldColumns, typeFilter]);

  // Sort rows
  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    const dir = sort.direction === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      switch (sort.field) {
        case 'name':
          return dir * a.resource.name.localeCompare(b.resource.name);
        case 'category':
          return dir * a.resource.category.localeCompare(b.resource.category);
        case 'entityCount':
          return dir * (a.entityCount - b.entityCount);
        default: {
          // Sort by field value
          const aVal = a.fieldValues.get(sort.field) ?? -Infinity;
          const bVal = b.fieldValues.get(sort.field) ?? -Infinity;
          return dir * (aVal - bVal);
        }
      }
    });
    return sorted;
  }, [filteredRows, sort]);

  // Compute totals for filtered rows
  const filteredTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const col of visibleColumns) {
      const values: number[] = [];
      for (const row of filteredRows) {
        const val = row.fieldValues.get(col.fieldId);
        if (val !== undefined) {
          values.push(col.aggregate === 'sum' ? val * row.entityCount : val);
        }
      }
      const result = computeAggregate(col.aggregate, values);
      if (result !== undefined) totals.set(col.fieldId, result);
    }
    return totals;
  }, [filteredRows, visibleColumns]);

  if (!isOpen) return null;

  const { summary } = overview;
  const costDisplay = summary.totalCostMonthly !== null
    ? `${formatAggregatedValue({ type: 'currency', value: summary.totalCostMonthly, unit: '\u20ac' })}${t('resource.canvasOverview.perMonth')}`
    : summary.totalCostYearly !== null
      ? `${formatAggregatedValue({ type: 'currency', value: summary.totalCostYearly, unit: '\u20ac' })}${t('resource.canvasOverview.perYear')}`
      : null;

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', zIndex: 60,
        }}
        onClick={onClose}
      />
      {/* Panel */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 61,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16, pointerEvents: 'none',
        }}
      >
        <div
          ref={modalRef}
          style={{
            backgroundColor: '#fff', borderRadius: 8,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.1)',
            width: '100%', maxWidth: 1024, maxHeight: '80vh',
            display: 'flex', flexDirection: 'column',
            pointerEvents: 'auto',
            ...(position ? { position: 'fixed', left: position.x, top: position.y } : {}),
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 16px', borderBottom: '1px solid #e2e8f0',
              cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none',
            }}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>
              {t('resource.canvasOverview.title')}
            </span>
            <button
              onClick={onClose}
              onMouseDown={e => e.stopPropagation()}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24, border: 'none', borderRadius: 4,
                background: 'transparent', cursor: 'pointer', color: '#6b7280',
              }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {overview.rows.length === 0 ? (
              <EmptyState message={t('resource.canvasOverview.noResources')} />
            ) : (
              <>
                {/* Summary Cards */}
                <div style={{ display: 'flex', gap: 12, padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                  {costDisplay && (
                    <SummaryCard icon={<DollarSign size={14} />} value={costDisplay} label={t('resource.canvasOverview.totalCost')} color="#10b981" />
                  )}
                  <SummaryCard icon={<Package size={14} />} value={String(summary.totalResources)} label={t('resource.canvasOverview.totalResources')} color="#6366f1" />
                  <SummaryCard icon={<Layers size={14} />} value={String(summary.totalEntitiesWithResources)} label={t('resource.canvasOverview.totalEntities')} color="#f59e0b" />
                </div>

                {/* Filter Bar */}
                <div style={{ display: 'flex', gap: 8, padding: '8px 16px', borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    placeholder={t('resource.canvasOverview.search')}
                    style={{
                      flex: 1, fontSize: 11, padding: '4px 8px',
                      border: '1px solid #e2e8f0', borderRadius: 4,
                      outline: 'none', backgroundColor: '#fff', color: '#1f2937',
                    }}
                  />
                  <select
                    value={categoryFilter}
                    onChange={e => setCategoryFilter(e.target.value)}
                    style={{
                      fontSize: 11, padding: '4px 6px', border: '1px solid #e2e8f0',
                      borderRadius: 4, outline: 'none', backgroundColor: '#fff', color: '#374151',
                    }}
                  >
                    <option value="">{t('resource.canvasOverview.allCategories')}</option>
                    {overview.categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <select
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                    style={{
                      fontSize: 11, padding: '4px 6px', border: '1px solid #e2e8f0',
                      borderRadius: 4, outline: 'none', backgroundColor: '#fff', color: '#374151',
                    }}
                  >
                    <option value="">{t('resource.canvasOverview.allTypes')}</option>
                    {overview.fieldTypes.map(ft => (
                      <option key={ft} value={ft}>{t(`resource.field.types.${ft}`)}</option>
                    ))}
                  </select>
                </div>

                {/* Table */}
                <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                        <SortableHeader
                          label={t('resource.canvasOverview.name')}
                          field="name"
                          sort={sort}
                          onSort={toggleSort}
                          style={{ textAlign: 'left', paddingLeft: 16, minWidth: 160 }}
                        />
                        <SortableHeader
                          label={t('resource.canvasOverview.category')}
                          field="category"
                          sort={sort}
                          onSort={toggleSort}
                          style={{ textAlign: 'left', minWidth: 80 }}
                        />
                        {visibleColumns.map(col => (
                          <SortableHeader
                            key={col.fieldId}
                            label={col.key}
                            field={col.fieldId}
                            sort={sort}
                            onSort={toggleSort}
                            style={{ textAlign: 'right', minWidth: 80 }}
                          />
                        ))}
                        <SortableHeader
                          label={t('resource.canvasOverview.entities')}
                          field="entityCount"
                          sort={sort}
                          onSort={toggleSort}
                          style={{ textAlign: 'right', paddingRight: 16, minWidth: 70 }}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={3 + visibleColumns.length}
                            style={{ padding: '24px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 11 }}
                          >
                            {t('resource.canvasOverview.noResults')}
                          </td>
                        </tr>
                      ) : (
                        <>
                          {sortedRows.map(row => (
                            <ResourceRow
                              key={row.resourceId}
                              row={row}
                              columns={visibleColumns}
                              isExpanded={expandedRows.has(row.resourceId)}
                              onToggleExpand={toggleExpand}
                            />
                          ))}
                          {/* Totals Row */}
                          <tr style={{ borderTop: '2px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                            <td style={{ padding: '6px 8px 6px 16px', fontWeight: 600, color: '#1f2937' }}>
                              {t('resource.canvasOverview.total')}
                            </td>
                            <td />
                            {visibleColumns.map(col => {
                              const total = filteredTotals.get(col.fieldId);
                              return (
                                <td key={col.fieldId} style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#1f2937' }}>
                                  {total !== undefined
                                    ? formatAggregatedValue({ type: col.type, value: total, unit: col.unit, interval: col.interval })
                                    : '\u2014'}
                                </td>
                              );
                            })}
                            <td />
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

// ============================================
// Sub-components
// ============================================

function SummaryCard({ icon, value, label, color }: { icon: React.ReactNode; value: string; label: string; color: string }) {
  return (
    <div style={{
      flex: 1, display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 12px', borderRadius: 6,
      backgroundColor: color + '08', border: `1px solid ${color}20`,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 6,
        backgroundColor: color + '15', color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{label}</div>
      </div>
    </div>
  );
}

function SortableHeader({ label, field, sort, onSort, style }: {
  label: string;
  field: string;
  sort: SortState;
  onSort: (field: string) => void;
  style?: React.CSSProperties;
}) {
  const isActive = sort.field === field;
  return (
    <th
      onClick={() => onSort(field)}
      style={{
        padding: '6px 8px', fontWeight: 500, color: isActive ? '#1f2937' : '#6b7280',
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontSize: 10,
        ...style,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
        {label}
        {isActive ? (
          sort.direction === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />
        ) : (
          <ArrowUpDown size={10} style={{ opacity: 0.3 }} />
        )}
      </span>
    </th>
  );
}

function ResourceRow({ row, columns, isExpanded, onToggleExpand }: {
  row: ResourceUsageRow;
  columns: FieldColumn[];
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
}) {
  return (
    <>
      <tr
        style={{ borderBottom: '1px solid #f1f5f9' }}
        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8fafc')}
        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
      >
        {/* Name with color dot */}
        <td style={{ padding: '6px 8px 6px 16px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              backgroundColor: row.resource.color || '#6366f1',
            }} />
            <span style={{ color: '#1f2937', fontWeight: 500 }}>{row.resource.name}</span>
          </span>
        </td>
        {/* Category */}
        <td style={{ padding: '6px 8px', color: '#6b7280' }}>
          {row.resource.category || '\u2014'}
        </td>
        {/* Dynamic field columns */}
        {columns.map(col => {
          const val = row.fieldValues.get(col.fieldId);
          return (
            <td key={col.fieldId} style={{ padding: '6px 8px', textAlign: 'right', color: '#374151', fontVariantNumeric: 'tabular-nums' }}>
              {val !== undefined
                ? formatAggregatedValue({ type: col.type, value: val, unit: col.unit, interval: col.interval })
                : '\u2014'}
            </td>
          );
        })}
        {/* Entity count */}
        <td style={{ padding: '6px 8px', textAlign: 'right', paddingRight: 16 }}>
          <button
            onClick={() => onToggleExpand(row.resourceId)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: 11, color: '#3b82f6', background: 'none', border: 'none',
              cursor: 'pointer', padding: 0,
            }}
          >
            {row.entityCount}
            {isExpanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
          </button>
        </td>
      </tr>
      {/* Expanded entity list */}
      {isExpanded && (
        <tr style={{ backgroundColor: '#f8fafc' }}>
          <td colSpan={3 + columns.length} style={{ padding: '4px 16px 8px 40px' }}>
            <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 2, fontWeight: 500 }}>
              {row.entityCount} {row.entityCount === 1 ? 'Entity' : 'Entities'}:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {row.entityIds.map(eid => (
                <span
                  key={eid}
                  style={{
                    fontSize: 10, padding: '1px 6px', borderRadius: 3,
                    backgroundColor: '#e2e8f0', color: '#374151',
                  }}
                >
                  {row.entityNames.get(eid) || eid}
                </span>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div style={{
      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 32,
    }}>
      <div style={{ textAlign: 'center' }}>
        <Package size={32} style={{ color: '#d1d5db', marginBottom: 8, margin: '0 auto' }} />
        <div style={{ fontSize: 12, color: '#6b7280', maxWidth: 280 }}>
          {message}
        </div>
      </div>
    </div>
  );
}
